import React from "react";
import { Link } from "react-router-dom";
import { PAGE_FAQS } from "../seo";
import { ROUTES } from "../routes";
import "./CalculatorSeoSection.css";

export type CalculatorSeoVariant =
  | "car"
  | "home"
  | "bond"
  | "tax"
  | "monthly";

const INTRO: Record<
  CalculatorSeoVariant,
  { title: string; paragraphs: string[] }
> = {
  car: {
    title: "About this car finance calculator",
    paragraphs: [
      "Use this free car affordability calculator to estimate your monthly vehicle finance repayment in South Africa. Enter the purchase price, deposit, annual interest rate, loan term, and optional balloon payment to see total interest and amount repayable.",
      "If you add your monthly net income, Totties shows whether the instalment fits common affordability guidelines — helping you decide before you visit the dealership or bank.",
    ],
  },
  home: {
    title: "About this home affordability calculator",
    paragraphs: [
      "This home loan affordability calculator helps you estimate how much property you can buy based on gross monthly income, deposit, interest rate, and bond term. It is designed for South African buyers, including joint applications with a co-owner.",
      "Results include maximum loan amount, estimated monthly repayment, and affordability status so you can compare options with your bond originator or bank.",
    ],
  },
  bond: {
    title: "About this bond repayment calculator",
    paragraphs: [
      "Calculate South African home loan repayments with this free bond calculator. Model standard instalments plus extra monthly payments, once-off lump sums, and future interest rate changes to see how much interest you could save.",
      "The amortisation chart compares scenarios side by side — useful when planning to pay ahead on your mortgage or preparing for a repo rate change.",
    ],
  },
  tax: {
    title: "About this SARS tax calculator",
    paragraphs: [
      "Estimate income tax, UIF, and take-home pay for the 2025/2026 South African tax year. Select your age group for rebates, add multiple taxable income streams, and export results to Excel.",
      "This tool uses current SARS tax brackets and is intended for planning — not as a substitute for official SARS advice or a registered tax practitioner.",
    ],
  },
  monthly: {
    title: "About this monthly budget planner",
    paragraphs: [
      "Plan household or personal monthly finances in one place. Track income and expenses, split shared costs between two or more people, and see remaining budget per person.",
      "Ideal for couples, flatmates, or solo budgeting — with PDF export when you want a snapshot to share or keep on file.",
    ],
  },
};

const RELATED: Record<CalculatorSeoVariant, { to: string; label: string }[]> = {
  car: [
    { to: ROUTES.homeAffordability, label: "Home affordability" },
    { to: ROUTES.bondRepayment, label: "Bond repayment" },
    { to: ROUTES.tax, label: "Tax calculator" },
  ],
  home: [
    { to: ROUTES.bondRepayment, label: "Bond repayment" },
    { to: ROUTES.carAffordability, label: "Car affordability" },
    { to: ROUTES.tax, label: "Tax calculator" },
  ],
  bond: [
    { to: ROUTES.homeAffordability, label: "Home affordability" },
    { to: ROUTES.carAffordability, label: "Car affordability" },
    { to: ROUTES.monthlyFinances, label: "Monthly finances" },
  ],
  tax: [
    { to: ROUTES.monthlyFinances, label: "Monthly finances" },
    { to: ROUTES.carAffordability, label: "Car affordability" },
    { to: ROUTES.bondRepayment, label: "Bond repayment" },
  ],
  monthly: [
    { to: ROUTES.tax, label: "Tax calculator" },
    { to: ROUTES.bondRepayment, label: "Bond repayment" },
    { to: ROUTES.homeAffordability, label: "Home affordability" },
  ],
};

const FAQ_PATH: Record<CalculatorSeoVariant, string> = {
  car: ROUTES.carAffordability,
  home: ROUTES.homeAffordability,
  bond: ROUTES.bondRepayment,
  tax: ROUTES.tax,
  monthly: ROUTES.monthlyFinances,
};

interface CalculatorSeoSectionProps {
  variant: CalculatorSeoVariant;
}

const CalculatorSeoSection: React.FC<CalculatorSeoSectionProps> = ({
  variant,
}) => {
  const intro = INTRO[variant];
  const faqs = PAGE_FAQS[FAQ_PATH[variant]] ?? [];
  const related = RELATED[variant];

  return (
    <aside className="calculator-seo" aria-labelledby={`seo-heading-${variant}`}>
      <h2 id={`seo-heading-${variant}`} className="calculator-seo-title">
        {intro.title}
      </h2>
      {intro.paragraphs.map((p) => (
        <p key={p.slice(0, 40)} className="calculator-seo-text">
          {p}
        </p>
      ))}

      {faqs.length > 0 && (
        <section aria-labelledby={`faq-heading-${variant}`}>
          <h3 id={`faq-heading-${variant}`} className="calculator-seo-subtitle">
            Frequently asked questions
          </h3>
          <dl className="calculator-seo-faq">
            {faqs.map((faq) => (
              <div key={faq.question} className="calculator-seo-faq-item">
                <dt>{faq.question}</dt>
                <dd>{faq.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      <nav className="calculator-seo-related" aria-label="Related calculators">
        <h3 className="calculator-seo-subtitle">Related tools</h3>
        <ul>
          {related.map(({ to, label }) => (
            <li key={to}>
              <Link to={to}>{label}</Link>
            </li>
          ))}
          <li>
            <Link to={ROUTES.home}>All calculators</Link>
          </li>
        </ul>
      </nav>
    </aside>
  );
};

export default CalculatorSeoSection;
